import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import { bindDialog, type PopupState } from "material-ui-popup-state/hooks";
import { useSnackbar } from "notistack";
import { FormattedMessage } from "react-intl";
import * as yup from "yup";

import FormikAutocomplete from "components/forms/mui/FormikAutocomplete";
import FormikCheckbox from "components/forms/mui/FormikCheckbox";
import FormikTextField from "components/forms/mui/FormikTextField";
import { SnackbarActionIcon } from "components/providers/ReactQueryProvider";
import { useUserStore } from "components/stores/UserStore";

import { useSendasupportrequestWIP } from "../../api/generated/v2/support";

const topicsOptions = [
  { value: "general", label: "General" },
  { value: "technical", label: "Technical" },
  { value: "invoicing", label: "Invoicing" },
  { value: "other", label: "Other" },
];

const schema = yup.object().shape({
  email: yup.string().email(),
  topic: yup.string().required(),
  description: yup.string().required().max(1000),
  consentToFurtherContact: yup.boolean().required(),
});

type FormValues = yup.InferType<typeof schema>;

function HelpDialog({ ...popupState }: PopupState) {
  const { user } = useUserStore();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isSuccess, setIsSuccess] = useState(false);

  const { mutateAsync: sendFeedback } = useSendasupportrequestWIP({
    mutation: {
      onSuccess: () => {
        enqueueSnackbar(
          <FormattedMessage defaultMessage="An email confirmation is on its way to you shortly. If you don't see it in your inbox, please take a moment to check your spam folder, just in case." />,
          {
            variant: "success",
            action: (key) => (
              <SnackbarActionIcon
                action={() => {
                  closeSnackbar(key);
                }}
              />
            ),
            key: "feddback-help-success",
            persist: true,
          },
        );
      },
    },
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      email: user?.UserV2?.user?.email || "",
      description: "",
      topic: "",
      consentToFurtherContact: false,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      await sendFeedback({
        data: {
          MayContactMe: values.consentToFurtherContact ? "yes" : "no",
          Text: values.description,
          Topic: values.topic,
          Type: "question",
          Url: btoa(window.location.href),
        },
      });
      formik.resetForm();
      setIsSuccess(true);
    },
    validationSchema: schema,
  });

  const handleClose = () => {
    formik.resetForm();
    setIsSuccess(false);
    popupState.close();
  };

  return (
    <Dialog {...bindDialog(popupState)} onClose={handleClose} maxWidth="sm">
      <FormikProvider value={formik}>
        <Form noValidate>
          <DialogTitle>
            {isSuccess ? (
              <FormattedMessage defaultMessage={"Submission successfull!"} />
            ) : (
              <FormattedMessage defaultMessage={"How can we help?"} />
            )}
          </DialogTitle>
          <Divider />
          <DialogContent>
            {isSuccess ? (
              <Alert severity="success">
                <FormattedMessage defaultMessage="An email confirmation is on its way to you shortly. If you don't see it in your inbox, please take a moment to check your spam folder, just in case." />
              </Alert>
            ) : (
              <>
                {" "}
                <Stack direction="row" justifyContent="center">
                  <svg
                    width="92"
                    height="107"
                    viewBox="0 0 92 107"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="46"
                      cy="53.5"
                      r="44"
                      fill="#E9F5FE"
                      stroke="#64B6F7"
                      strokeWidth="3"
                    />
                    <path
                      d="M62.9062 35.8125H30.0938C27.0176 35.8125 24.625 38.2905 24.625 41.2812V63.1562C24.625 66.2324 27.0176 68.625 30.0938 68.625H62.9062C65.897 68.625 68.375 66.2324 68.375 63.1562V41.2812C68.375 38.2905 65.897 35.8125 62.9062 35.8125ZM30.0938 39.9141H62.9062C63.5898 39.9141 64.2734 40.5977 64.2734 41.2812V43.2466L50.0034 55.0386C48.0381 56.6621 44.8765 56.6621 42.9111 55.0386L28.7266 43.2466V41.2812C28.7266 40.5977 29.3247 39.9141 30.0938 39.9141ZM62.9062 64.5234H30.0938C29.3247 64.5234 28.7266 63.9253 28.7266 63.1562V48.5444L40.3477 58.2856C42.0566 59.6528 44.1929 60.4219 46.5 60.4219C48.7217 60.4219 50.8579 59.6528 52.5669 58.2856L64.2734 48.5444V63.1562C64.2734 63.9253 63.5898 64.5234 62.9062 64.5234Z"
                      fill="#64B6F7"
                    />
                  </svg>
                </Stack>
                <p>
                  <FormattedMessage
                    values={{ br: <br /> }}
                    defaultMessage="We're here to help! Use this form to get support, ask a question, report a bug, or share your ideas for improvements.
                {br}{br}

Fill in the details below, and we'll get back to you via email as soon as possible. Your feedback helps us create a better experience, and we appreciate you being part of our journey!"
                  />
                </p>
                <FormikAutocomplete
                  name="topic"
                  label={
                    <FormattedMessage defaultMessage="What do you need help with?" />
                  }
                  helperText={
                    <FormattedMessage defaultMessage="Please choose the topic that best fits your question or concern. " />
                  }
                  options={topicsOptions}
                />
                <FormikTextField
                  name="email"
                  label={<FormattedMessage defaultMessage="Email" />}
                />
                <FormikTextField
                  name="description"
                  label={
                    <FormattedMessage defaultMessage="Describe your question" />
                  }
                  multiline
                  minRows={4}
                  helperText={
                    <FormattedMessage
                      defaultMessage={
                        "{charactersLeft}/1000 characters remaining"
                      }
                      values={{
                        charactersLeft: 1000 - formik.values.description.length,
                      }}
                    />
                  }
                />
                <FormikCheckbox
                  name="consentToFurtherContact"
                  label={
                    <FormattedMessage defaultMessage="Plug AS may contact me for further details." />
                  }
                />
                <Typography variant="body2">
                  <FormattedMessage defaultMessage="By submitting this form, you agree that your email address and the URL of the page are attached to the ticket." />
                </Typography>
              </>
            )}
          </DialogContent>

          {!isSuccess ? (
            <Stack
              direction="row"
              gap={1}
              justifyContent="space-between"
              sx={{ p: 1 }}
            >
              <Button
                type="button"
                onClick={handleClose}
                fullWidth
                sx={{ height: "42px" }}
              >
                <FormattedMessage defaultMessage="Cancel" />
              </Button>
              <Button variant="contained" type="submit" fullWidth>
                <FormattedMessage defaultMessage="Submit request" />
              </Button>
            </Stack>
          ) : (
            <>
              <Divider />
              <DialogActions>
                <Button type="button" onClick={handleClose}>
                  <FormattedMessage defaultMessage="Close" />
                </Button>
              </DialogActions>
            </>
          )}
        </Form>
      </FormikProvider>
    </Dialog>
  );
}

export default HelpDialog;
